import React, { useState, useEffect } from 'react';
import { Card, Row, Col, Button, Modal, Badge, Spinner, Alert } from 'react-bootstrap';
import { Search, Download, Eye, Grid, List, Image as ImageIcon, Filter, RefreshCw, User, Car, CreditCard, AlertCircle, Lock } from 'lucide-react';
import { API_URL, API_URL_UPLOADS } from './config';
import { useAuth } from './auth/AuthProvider';

const ImageGallery = () => {
  const { isAuthenticated, user, isLoading } = useAuth();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [filter, setFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [imagesPerPage] = useState(20);
  const [stats, setStats] = useState({ customers: 0, drivers: 0, vehicles: 0, documents: 0, orphaned: 0 });
  const [hasFetched, setHasFetched] = useState(false);

  // Use the same authentication method as the sidebar
  const isUserAuthenticated = () => {
    const userName = localStorage.getItem("userName");
    const adminId = localStorage.getItem("adminId");
    const userDetails = localStorage.getItem("userDetails");
    
    console.log('ImageGallery Auth Check (same as sidebar):', {
      userName: !!userName,
      adminId: !!adminId,
      userDetails: !!userDetails,
      userNameValue: userName
    });
    
    // If no username is set, user is not logged in
    if (!userName || userName.trim() === '') {
      console.log('No username found in ImageGallery - not authenticated');
      return false;
    }
    
    console.log('User authenticated in ImageGallery:', userName);
    return true;
  };

  const authenticated = isUserAuthenticated();

  // Debug authentication state
  useEffect(() => {
    console.log('ImageGallery Auth State:', { 
      isAuthenticated, 
      isLoading, 
      user,
      authenticated: authenticated
    });
  }, [isAuthenticated, isLoading, user, authenticated]);

  useEffect(() => {
    if (authenticated && !isLoading && !hasFetched) {
      console.log('Starting image fetch...');
      fetchImages();
      setHasFetched(true);
    }
  }, [authenticated, isLoading, hasFetched]);

  const fetchImages = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const allImages = [];
      let customerCount = 0, driverCount = 0, vehicleCount = 0, documentCount = 0;
      
      console.log('Fetching images from database...');
      
      // Fetch customer images
      try {
        const customerResponse = await fetch(`${API_URL}/customerdetails/`);
        if (customerResponse.ok) {
          const customers = await customerResponse.json();
          console.log(`Processing ${customers.length} customers for images`);
          
          customers.forEach(customer => {
            // Profile pictures
            if (customer.profilePic && customer.profilePic.trim()) {
              const imageUrl = buildImageUrl(customer.profilePic);
              if (imageUrl) {
                allImages.push(createImageData(customer.profilePic, 'customer-profile', customer));
                customerCount++;
              }
            }
            
            // ID images
            if (customer.id_image && customer.id_image.trim()) {
              const imageUrl = buildImageUrl(customer.id_image);
              if (imageUrl) {
                allImages.push(createImageData(customer.id_image, 'customer-id', customer));
                documentCount++;
              }
            }
          });
        } else {
          console.log('Customer endpoint returned:', customerResponse.status);
          if (customerResponse.status === 404) {
            console.log('Customer endpoint not found - using empty array');
          }
        }
      } catch (err) {
        console.error('Error fetching customer images:', err);
        // Don't set error for network issues, just continue with empty data
      }
      
      // Fetch driver images
      try {
        const driverResponse = await fetch(`${API_URL}/driver/`);
        if (driverResponse.ok) {
          const drivers = await driverResponse.json();
          console.log(`Processing ${drivers.length} drivers for images`);
          
          drivers.forEach(driver => {
            // Profile pictures
            if (driver.profilePic && driver.profilePic.trim()) {
              const imageUrl = buildImageUrl(driver.profilePic);
              if (imageUrl) {
                allImages.push(createImageData(driver.profilePic, 'driver-profile', driver));
                driverCount++;
              }
            }
            
            // ID images
            if (driver.id_image && driver.id_image.trim()) {
              const imageUrl = buildImageUrl(driver.id_image);
              if (imageUrl) {
                allImages.push(createImageData(driver.id_image, 'driver-id', driver));
                documentCount++;
              }
            }
            
            // Driver license
            if (driver.driver_license_image && driver.driver_license_image.trim()) {
              const imageUrl = buildImageUrl(driver.driver_license_image);
              if (imageUrl) {
                allImages.push(createImageData(driver.driver_license_image, 'driver-license', driver));
                documentCount++;
              }
            }
            
            // Number plate
            if (driver.number_plate_image && driver.number_plate_image.trim()) {
              const imageUrl = buildImageUrl(driver.number_plate_image);
              if (imageUrl) {
                allImages.push(createImageData(driver.number_plate_image, 'driver-plate', driver));
                documentCount++;
              }
            }
            
            // Vehicle license
            if (driver.vehicle_license_image && driver.vehicle_license_image.trim()) {
              const imageUrl = buildImageUrl(driver.vehicle_license_image);
              if (imageUrl) {
                allImages.push(createImageData(driver.vehicle_license_image, 'vehicle-license', driver));
                documentCount++;
              }
            }
            
            // Vehicle images
            ['vehicle_img1', 'vehicle_img2', 'vehicle_img3'].forEach((field, index) => {
              if (driver[field] && driver[field].trim()) {
                const imageUrl = buildImageUrl(driver[field]);
                if (imageUrl) {
                  allImages.push(createImageData(driver[field], `vehicle-${index + 1}`, driver));
                  vehicleCount++;
                }
              }
            });
          });
        } else {
          console.log('Driver endpoint returned:', driverResponse.status);
          if (driverResponse.status === 404) {
            console.log('Driver endpoint not found - using empty array');
          }
        }
      } catch (err) {
        console.error('Error fetching driver images:', err);
        // Don't set error for network issues, just continue with empty data
      }

      // Remove duplicates and sort
      const uniqueImages = allImages
        .filter((image, index, self) => index === self.findIndex((img) => img.url === image.url))
        .sort((a, b) => a.name.localeCompare(b.name));

      // Fetch unassociated/orphaned images
      try {
        console.log('Note: Orphaned image scanning requires backend endpoint /uploads/list');
        console.log('Currently showing only database-associated images');
        
        // For now, we'll add a placeholder orphaned image for demonstration
        // This can be removed when the backend endpoint is available
        if (uniqueImages.length === 0) {
          // Add sample orphaned image for testing (remove when endpoint is ready)
          uniqueImages.push({
            url: `${API_URL_UPLOADS}/sample_orphaned.jpg`,
            name: 'Sample_Orphaned_Image',
            path: '/sample_orphaned.jpg',
            type: 'jpg',
            source: 'orphaned',
            ownerInfo: 'Unassociated Image - Demo',
            owner: null,
            verified: false
          });
        }
      } catch (err) {
        console.log('Could not fetch orphaned images:', err.message);
      }

      setImages(uniqueImages);
      setStats({ 
        customers: customerCount, 
        drivers: driverCount, 
        vehicles: vehicleCount, 
        documents: documentCount,
        orphaned: uniqueImages.filter(img => img.source === 'orphaned').length
      });
      
      console.log(`Successfully loaded ${uniqueImages.length} unique images`);
      console.log(`Stats: ${JSON.stringify({ customers: customerCount, drivers: driverCount, vehicles: vehicleCount, documents: documentCount, orphaned: uniqueImages.filter(img => img.source === 'orphaned').length })}`);
      
    } catch (err) {
      console.error('Error in fetchImages:', err);
      setError(`Failed to load images: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const buildImageUrl = (imagePath) => {
    if (!imagePath || typeof imagePath !== 'string' || imagePath.trim() === '') {
      return null;
    }
    
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    return `${API_URL_UPLOADS}${cleanPath}`;
  };

  const createImageData = (imagePath, source, owner) => {
    const cleanPath = imagePath.startsWith('/') ? imagePath : `/${imagePath}`;
    const url = buildImageUrl(imagePath);
    const type = cleanPath.split('.').pop().toLowerCase();
    
    let name = '';
    let ownerInfo = '';
    
    if (source.includes('customer')) {
      name = `Customer_${source.replace('customer-', '').toUpperCase()}_${owner.name || 'Unknown'}_${owner.surname || ''}`;
      ownerInfo = `${owner.name || 'Unknown'} ${owner.surname || ''}`.trim();
    } else if (source.includes('driver')) {
      name = `Driver_${source.replace('driver-', '').toUpperCase()}_${owner.name || 'Unknown'}_${owner.surname || ''}`;
      ownerInfo = `${owner.name || 'Unknown'} ${owner.surname || ''}`.trim();
      
      if (source.includes('vehicle')) {
        const vehicleInfo = owner.make && owner.model ? ` - ${owner.make} ${owner.model}` : '';
        name += vehicleInfo;
        ownerInfo += vehicleInfo;
      }
    }
    
    return {
      url,
      name: name.replace(/\s+/g, '_'),
      path: cleanPath,
      type,
      source,
      ownerInfo,
      owner,
      verified: true
    };
  };

  const filteredImages = images.filter(image => {
    const matchesSearch = image.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         image.ownerInfo?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || image.type === filter || image.source === filter;
    return matchesSearch && matchesFilter;
  });

  const indexOfLastImage = currentPage * imagesPerPage;
  const indexOfFirstImage = indexOfLastImage - imagesPerPage;
  const currentImages = filteredImages.slice(indexOfFirstImage, indexOfLastImage);
  const totalPages = Math.ceil(filteredImages.length / imagesPerPage);

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setShowModal(true);
  };

  const downloadImage = async (image) => {
    try {
      const response = await fetch(image.url);
      if (!response.ok) throw new Error('Failed to fetch image');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${image.name}.${image.type}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading image:', err);
      alert('Failed to download image. Please try again.');
    }
  };

  const getSourceIcon = (source) => {
    if (source === 'orphaned') return <AlertCircle size={16} className="text-danger" />;
    if (source.includes('profile')) return <User size={16} className="text-primary" />;
    if (source.includes('vehicle')) return <Car size={16} className="text-success" />;
    if (source.includes('id') || source.includes('license') || source.includes('plate')) return <CreditCard size={16} className="text-warning" />;
    return <ImageIcon size={16} className="text-info" />;
  };

  const getSourceColor = (source) => {
    if (source === 'orphaned') return 'danger';
    if (source.includes('profile')) return 'primary';
    if (source.includes('vehicle')) return 'success';
    if (source.includes('id') || source.includes('license') || source.includes('plate')) return 'warning';
    return 'info';
  };

  if (loading || isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Spinner animation="border" variant="primary" className="mb-3" />
          <p className="text-muted">
            {isLoading ? 'Checking authentication...' : 'Loading images from database...'}
          </p>
          <small className="text-muted">
            {isLoading ? 'Please wait...' : 'Fetching customer and driver images...'}
          </small>
        </div>
      </div>
    );
  }

  // Show authentication error if not authenticated
  if (!authenticated && !isLoading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <Lock size={48} className="text-danger mb-3" />
          <h4 className="text-danger mb-3">Authentication Required</h4>
          <p className="text-muted mb-4">
            You must be logged in to access the Image Gallery.
          </p>
          <div className="mb-3">
            <small className="text-muted">
              Auth Status: {authenticated ? 'Authenticated' : 'Not Authenticated'}
            </small>
            <br />
            <small className="text-muted">
              Loading: {isLoading ? 'Yes' : 'No'}
            </small>
            <br />
            <small className="text-muted">
              User: {user ? JSON.stringify(user) : 'None'}
            </small>
          </div>
          <Button variant="primary" onClick={() => window.location.href = '/login'}>
            Go to Login
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <Alert variant="danger" className="m-3">
        <Alert.Heading>Error Loading Images</Alert.Heading>
        <p>{error}</p>
        <Button variant="outline-danger" onClick={fetchImages} className="mt-2">
          <RefreshCw size={16} className="me-2" />
          Try Again
        </Button>
      </Alert>
    );
  }

  return (
    <div className="p-4">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h2 className="mb-1">Image Gallery</h2>
          <p className="text-muted mb-0">
            Customer and driver images from database
          </p>
        </div>
        <Button variant="outline-primary" onClick={fetchImages}>
          <RefreshCw size={16} className="me-2" />
          Refresh
        </Button>
      </div>

      {/* Enhanced Stats Cards */}
      <Row className="mb-4">
        <Col md={2}>
          <Card className="text-center border-primary">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <ImageIcon size={20} className="text-primary me-2" />
                <h3 className="text-primary mb-0">{images.length}</h3>
              </div>
              <p className="text-muted mb-0">Total Images</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-success">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <User size={20} className="text-success me-2" />
                <h3 className="text-success mb-0">{stats.customers + stats.drivers}</h3>
              </div>
              <p className="text-muted mb-0">Profile Pictures</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-warning">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <CreditCard size={20} className="text-warning me-2" />
                <h3 className="text-warning mb-0">{stats.documents}</h3>
              </div>
              <p className="text-muted mb-0">Documents</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-info">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Car size={20} className="text-info me-2" />
                <h3 className="text-info mb-0">{stats.vehicles}</h3>
              </div>
              <p className="text-muted mb-0">Vehicle Images</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-danger">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <AlertCircle size={20} className="text-danger me-2" />
                <h3 className="text-danger mb-0">{stats.orphaned}</h3>
              </div>
              <p className="text-muted mb-0">Unassociated</p>
            </Card.Body>
          </Card>
        </Col>
        <Col md={2}>
          <Card className="text-center border-secondary">
            <Card.Body>
              <div className="d-flex align-items-center justify-content-center mb-2">
                <Filter size={20} className="text-secondary me-2" />
                <h3 className="text-secondary mb-0">{filteredImages.length}</h3>
              </div>
              <p className="text-muted mb-0">Filtered</p>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Enhanced Controls */}
      <Card className="mb-4">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={4}>
              <div className="input-group">
                <span className="input-group-text">
                  <Search size={16} />
                </span>
                <input
                  type="text"
                  className="form-control"
                  placeholder="Search images by name or owner..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
              </div>
            </Col>
            <Col md={3}>
              <select
                className="form-select"
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
              >
                <option value="all">All Images</option>
                <option value="customer-profile">Customer Profiles</option>
                <option value="customer-id">Customer IDs</option>
                <option value="driver-profile">Driver Profiles</option>
                <option value="driver-id">Driver IDs</option>
                <option value="driver-license">Driver Licenses</option>
                <option value="driver-plate">Number Plates</option>
                <option value="vehicle-license">Vehicle Licenses</option>
                <option value="vehicle-1">Vehicle Images 1</option>
                <option value="vehicle-2">Vehicle Images 2</option>
                <option value="vehicle-3">Vehicle Images 3</option>
                <option value="orphaned">Unassociated Images</option>
                <option value="png">PNG</option>
                <option value="jpg">JPG</option>
                <option value="jpeg">JPEG</option>
                <option value="gif">GIF</option>
                <option value="webp">WebP</option>
                <option value="svg">SVG</option>
              </select>
            </Col>
            <Col md={3}>
              <div className="btn-group w-100" role="group">
                <Button
                  variant={viewMode === 'grid' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('grid')}
                  className="flex-fill"
                >
                  <Grid size={16} />
                </Button>
                <Button
                  variant={viewMode === 'list' ? 'primary' : 'outline-primary'}
                  onClick={() => setViewMode('list')}
                  className="flex-fill"
                >
                  <List size={16} />
                </Button>
              </div>
            </Col>
            <Col md={2}>
              <Badge bg="info" className="d-block text-center p-2">
                Page {currentPage} of {totalPages || 1}
              </Badge>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Images Display */}
      {currentImages.length === 0 ? (
        <Alert variant="info">
          <Eye size={16} className="me-2" />
          <div>
            <strong>No images found in database</strong>
            <br />
            <small className="text-muted">
              {images.length === 0 
                ? "There are currently no customer profiles or driver profiles with images in the database. Images will appear here once users upload their profile pictures, ID documents, and vehicle images during the signup process."
                : "No images match your current search criteria. Try adjusting your search or filters."}
            </small>
          </div>
        </Alert>
      ) : (
        <>
          {viewMode === 'grid' ? (
            <Row>
              {currentImages.map((image, index) => (
                <Col md={3} sm={6} className="mb-4" key={index}>
                  <Card className="h-100 image-card shadow-sm">
                    <div className="position-relative">
                      <img
                        src={image.url}
                        alt={image.name}
                        className="card-img-top"
                        style={{
                          height: '200px',
                          objectFit: 'cover',
                          cursor: 'pointer'
                        }}
                        onClick={() => handleImageClick(image)}
                        onLoad={(e) => {
                          console.log(`Successfully loaded image: ${image.name}`);
                        }}
                        onError={(e) => {
                          console.log(`Failed to load image: ${image.name} from ${image.url}`);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2Y4ZjlmOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2NzY4NjkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                        }}
                      />
                      <div className="position-absolute top-0 end-0 m-2">
                        <Badge bg={getSourceColor(image.source)} className="shadow">
                          {image.type.toUpperCase()}
                        </Badge>
                      </div>
                      <div className="position-absolute top-0 start-0 m-2">
                        <div className="d-flex align-items-center bg-white rounded p-1 shadow-sm">
                          {getSourceIcon(image.source)}
                          <Badge bg={getSourceColor(image.source)} className="ms-1" style={{ fontSize: '9px' }}>
                            {image.source.replace('-', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <Card.Body className="p-2">
                      <h6 className="mb-1 text-truncate" title={image.name}>
                        {image.name.replace(/_/g, ' ')}
                      </h6>
                      {image.ownerInfo && (
                        <small className="text-muted d-block mb-2">
                          <User size={12} className="me-1" />
                          {image.ownerInfo}
                        </small>
                      )}
                      <div className="d-flex justify-content-between align-items-center">
                        <small className="text-muted">
                          <ImageIcon size={12} className="me-1" />
                          {image.type.toUpperCase()}
                        </small>
                        <div>
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleImageClick(image)}
                            className="me-1"
                            title="View Image"
                          >
                            <Eye size={12} />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => downloadImage(image)}
                            title="Download Image"
                          >
                            <Download size={12} />
                          </Button>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              ))}
            </Row>
          ) : (
            <div className="table-responsive">
              <table className="table table-striped table-hover">
                <thead className="table-light">
                  <tr>
                    <th width="80">Preview</th>
                    <th>Name</th>
                    <th width="80">Type</th>
                    <th width="150">Owner</th>
                    <th width="120">Source</th>
                    <th width="100">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentImages.map((image, index) => (
                    <tr key={index} className="align-middle">
                      <td>
                        <img
                          src={image.url}
                          alt={image.name}
                          style={{
                            width: '50px',
                            height: '50px',
                            objectFit: 'cover',
                            cursor: 'pointer',
                            borderRadius: '4px',
                            border: '1px solid #dee2e6',
                          }}
                          onClick={() => handleImageClick(image)}
                          onLoad={(e) => {
                            console.log(`List view loaded image: ${image.name}`);
                          }}
                          onError={(e) => {
                            console.log(`List view failed image: ${image.name} from ${image.url}`);
                            e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNTAiIGhlaWdodD0iNTAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PHJlY3Qgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSIjZjhmOWY5Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxMCIgZmlsbD0iIzY3Njg2OSIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZG9taW5hbnQtYmFzZWxpbmU9Im1pZGRsZSI+Tm8gSW1hZ2U8L3RleHQ+PC9zdmc+';
                          }}
                        />
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getSourceIcon(image.source)}
                          <div className="ms-2">
                            <div className="fw-medium">{image.name.replace(/_/g, ' ')}</div>
                            {image.ownerInfo && (
                              <small className="text-muted">{image.ownerInfo}</small>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <Badge bg={getSourceColor(image.source)} className="text-uppercase">
                          {image.type}
                        </Badge>
                      </td>
                      <td>
                        <small className="text-muted">
                          {image.ownerInfo || 'N/A'}
                        </small>
                      </td>
                      <td>
                        <div className="d-flex align-items-center">
                          {getSourceIcon(image.source)}
                          <Badge bg={getSourceColor(image.source)} className="ms-1" style={{ fontSize: '10px' }}>
                            {image.source.replace('-', ' ').toUpperCase()}
                          </Badge>
                        </div>
                      </td>
                      <td>
                        <div className="btn-group">
                          <Button
                            variant="outline-primary"
                            size="sm"
                            onClick={() => handleImageClick(image)}
                            title="View Image"
                          >
                            <Eye size={12} />
                          </Button>
                          <Button
                            variant="outline-success"
                            size="sm"
                            onClick={() => downloadImage(image)}
                            title="Download Image"
                          >
                            <Download size={12} />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Enhanced Pagination */}
          {totalPages > 1 && (
            <div className="d-flex justify-content-between align-items-center mt-4">
              <div className="text-muted">
                Showing {indexOfFirstImage + 1} to {Math.min(indexOfLastImage, filteredImages.length)} of{' '}
                {filteredImages.length} images
              </div>
              <div className="btn-group">
                <Button
                  variant="outline-primary"
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage(currentPage - 1)}
                >
                  Previous
                </Button>
                {[...Array(totalPages)].map((_, index) => {
                  const pageNum = index + 1;
                  const showPage = pageNum === 1 || pageNum === totalPages || 
                                 (pageNum >= currentPage - 2 && pageNum <= currentPage + 2);
                  if (!showPage && pageNum === currentPage - 3) {
                    return <Button key="ellipsis-start" variant="outline-primary" disabled>...</Button>;
                  }
                  if (!showPage && pageNum === currentPage + 3) {
                    return <Button key="ellipsis-end" variant="outline-primary" disabled>...</Button>;
                  }
                  if (!showPage) return null;
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? 'primary' : 'outline-primary'}
                      onClick={() => setCurrentPage(pageNum)}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                <Button
                  variant="outline-primary"
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage(currentPage + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Enhanced Image Modal */}
      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="xl"
        centered
      >
        <Modal.Header closeButton className="bg-light">
          <Modal.Title className="d-flex align-items-center">
            {selectedImage && getSourceIcon(selectedImage.source)}
            <span className="ms-2">{selectedImage?.name?.replace(/_/g, ' ')}</span>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedImage && (
            <Row>
              <Col md={8}>
                <div className="text-center bg-light rounded p-3">
                  <img
                    src={selectedImage.url}
                    alt={selectedImage.name}
                    className="img-fluid rounded shadow"
                    style={{ maxHeight: '500px' }}
                    onLoad={(e) => {
                      console.log(`Modal loaded image: ${selectedImage.name}`);
                    }}
                    onError={(e) => {
                      console.log(`Modal failed image: ${selectedImage.name} from ${selectedImage.url}`);
                      e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAwIiBoZWlnaHQ9IjQwMCIgZmlsbD0iI2Y4ZjlmOSIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTgiIGZpbGw9IiM2NzY4NjkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiPkltYWdlIG5vdCBhdmFpbGFibGU8L3RleHQ+PC9zdmc+';
                    }}
                  />
                </div>
              </Col>
              <Col md={4}>
                <Card className="h-100">
                  <Card.Header>
                    <h5 className="mb-0">Image Details</h5>
                  </Card.Header>
                  <Card.Body>
                    <div className="mb-3">
                      <label className="text-muted small">Type</label>
                      <div className="d-flex align-items-center">
                        <Badge bg={getSourceColor(selectedImage.source)} className="text-uppercase">
                          {selectedImage.type}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-muted small">Source</label>
                      <div className="d-flex align-items-center">
                        {getSourceIcon(selectedImage.source)}
                        <Badge bg={getSourceColor(selectedImage.source)} className="ms-2" style={{ fontSize: '10px' }}>
                          {selectedImage.source.replace('-', ' ').toUpperCase()}
                        </Badge>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-muted small">Owner</label>
                      <div>
                        {selectedImage.ownerInfo ? (
                          <div className="d-flex align-items-center">
                            <User size={16} className="text-muted me-2" />
                            <span>{selectedImage.ownerInfo}</span>
                          </div>
                        ) : (
                          <span className="text-muted">N/A</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-muted small">File Path</label>
                      <div className="bg-light p-2 rounded small font-monospace text-break">
                        {selectedImage.path}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-muted small">Full URL</label>
                      <div className="bg-light p-2 rounded small font-monospace text-break">
                        {selectedImage.url}
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="text-muted small">Status</label>
                      <div>
                        <Badge bg="success">Verified Database Image</Badge>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          {selectedImage && (
            <Button variant="success" onClick={() => downloadImage(selectedImage)}>
              <Download size={16} className="me-2" />
              Download Image
            </Button>
          )}
        </Modal.Footer>
      </Modal>

      
      <style>{`
        .image-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
          transition: all 0.3s ease;
        }
        
        .image-card img {
          transition: transform 0.3s ease;
        }
        
        .image-card:hover img {
          transform: scale(1.05);
        }
      `}</style>
    </div>
  );
};

export default ImageGallery;